import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';
import ContractSelectionModal from './ContractSelectionModal';
import Icon from './Icon';
import i18n from '../i18n';
import { getOrderableContractsFromUser, shouldBlockOrdering } from '../utils/contractUtils';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';
import { canAccessTranslations } from '../utils/translationAccess';
import { showError } from '../services/toastService';

const PrimaryButton = styled.button`
  height: 50px;
  min-height: 50px;
  border: 1px solid ${({ color }) => color};
  border-right: none;
  border-radius: 50px 0 0 50px;
  background-color: ${({ color }) => color};
  color: #ffffff;
  cursor: pointer;
  padding: 0 ${({ padding }) => padding}px;
  font-size: ${({ fontSize }) => fontSize}px;
  text-transform: uppercase;
  white-space: nowrap;
  transition: ${({ transition }) => transition};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DropdownToggle = styled.button`
  height: 50px;
  min-height: 50px;
  border: 1px solid ${({ color }) => color};
  border-left: none;
  border-radius: 0 50px 50px 0;
  background-color: ${({ color }) => color};
  color: #ffffff;
  cursor: pointer;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ transition }) => transition};
`;

// The primary button and the dropdown toggle form a single split-button: hovering
// either part highlights the whole element together rather than each half on its own.
const Wrapper = styled.div`
  display: inline-flex;
  align-items: stretch;
  position: relative;

  &:hover ${PrimaryButton}:not(:disabled),
  &:hover ${DropdownToggle}:not(:disabled) {
    background-color: transparent;
    color: ${({ color }) => color};
    i {
      color: ${({ color }) => color} !important;
    }
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 100;
  background-color: #ffffff;
  border: 1px solid rgba(69, 68, 107, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 220px;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  width: 100%;
  display: block;
  background: none;
  border: none;
  padding: 12px 16px;
  font-size: ${({ fontSize }) => fontSize}px;
  color: ${({ color }) => color};
  text-align: left;
  cursor: pointer;
  &:hover {
    background-color: rgba(228, 228, 255, 0.5);
  }
`;

const HeroOrderSplitButton = ({ dataTest = 'heroOrderSplitButton' }) => {
  const { user } = useContext(AppContext);
  const { colors, layout } = useContext(ThemeContext);
  const history = useHistory();

  const [menuOpen, setMenuOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const wrapperRef = useRef(null);
  const isHero = isHeroBrandEnabled();
  const translationEnabled = canAccessTranslations(user);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Remember the page the order flow was started from, so the wizard's Cancel
  // button can return the user here instead of a hardcoded destination. Read at
  // click time from history (not useLocation) so the component renders safely
  // even when mounted without a Router context.
  const getReturnTo = () => {
    const loc = history?.location;
    return loc ? `${loc.pathname}${loc.search}` : undefined;
  };

  const startInterpretationOrder = (contractOverride = null) => {
    history.push('/book-interpretation', {
      selectedContractId: contractOverride?.id || null,
      selectedContractName: contractOverride?.name || null,
      returnTo: getReturnTo(),
    });
  };

  const handlePrebookClick = () => {
    setMenuOpen(false);
    if (shouldBlockOrdering(user)) {
      showError(i18n('no_orderable_contract', 'No contract is available for your languages. Please contact support.'), 'no-orderable-contract');
      return;
    }
    const orderableContracts = getOrderableContractsFromUser(user);
    if (orderableContracts.length <= 1) {
      startInterpretationOrder(orderableContracts[0] || null);
      return;
    }
    setContractModalOpen(true);
  };

  const handleContractSelected = (contract) => {
    setContractModalOpen(false);
    startInterpretationOrder(contract);
  };

  const handleTranslationClick = () => {
    setMenuOpen(false);
    if (shouldBlockOrdering(user)) {
      showError(i18n('no_orderable_contract', 'No contract is available for your languages. Please contact support.'), 'no-orderable-contract');
      return;
    }
    history.push('/order-translation', { returnTo: getReturnTo() });
  };

  return (
    <Wrapper ref={wrapperRef} color={colors.containerColor} data-test={dataTest}>
      <PrimaryButton
        type="button"
        color={colors.containerColor}
        padding={layout.smallButtonPadding}
        fontSize={layout.fontSize}
        transition={layout.transition}
        onClick={isHero ? () => setMenuOpen((open) => !open) : handlePrebookClick}
        data-test={`${dataTest}-primary`}
      >
        {isHero && (
          <Icon
            icon="add"
            iconLib="material"
            iconColor="#ffffff"
            iconSize={20}
            withoutContainer
          />
        )}
        {(isHero ? i18n('order') : i18n('pre_book')).toUpperCase()}
      </PrimaryButton>
      <DropdownToggle
        type="button"
        color={colors.containerColor}
        transition={layout.transition}
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        data-test={`${dataTest}-toggle`}
      >
        <Icon
          icon={menuOpen ? 'expand_less' : 'expand_more'}
          iconLib="material"
          iconColor="#ffffff"
          iconSize={20}
          withoutContainer
        />
      </DropdownToggle>
      {menuOpen && (
        <DropdownList role="menu" data-test={`${dataTest}-menu`}>
          <DropdownItem
            type="button"
            role="menuitem"
            color={colors.webDarkBlue}
            fontSize={layout.fontSize}
            onClick={handlePrebookClick}
            data-test={`${dataTest}-item-prebook`}
          >
            {isHero ? i18n('hero_order_interpretation') : i18n('pre_book')}
          </DropdownItem>
          {translationEnabled && (
            <DropdownItem
              type="button"
              role="menuitem"
              color={colors.webDarkBlue}
              fontSize={layout.fontSize}
              onClick={handleTranslationClick}
              data-test={`${dataTest}-item-translation`}
            >
              {isHero ? i18n('hero_order_translation') : i18n('order_translation')}
            </DropdownItem>
          )}
        </DropdownList>
      )}
      <ContractSelectionModal
        isOpen={contractModalOpen}
        contracts={getOrderableContractsFromUser(user)}
        colors={colors}
        onSelect={handleContractSelected}
        onClose={() => setContractModalOpen(false)}
      />
    </Wrapper>
  );
};

export default HeroOrderSplitButton;
